import { useQuery } from '@apollo/client';
import {
  faExclamationTriangle,
  faExternalLink,
  faPencil,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { Confirm } from '~/components/common/Confirm';
import { DropdownMenu } from '~/components/common/DropdownMenu';
import { FilterSearch } from '~/components/common/FilterSearch';
import { Heading } from '~/components/common/Heading';
import { NoItemsRow } from '~/components/common/NoItemsRow';
import { PageHeading } from '~/components/common/PageHeading';
import { Panel } from '~/components/common/Panel';
import { SortTrigger } from '~/components/common/SortTrigger';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Alert } from '~/components/ui/alert';
import { DeleteOtherWiki } from '~/components/upload/other-wiki/delete-other-wiki';
import { useSortFilter } from '~/hooks/data';
import {
  otherWikiDetailRoute,
  uploadOtherWikiCreateRoute,
  uploadOtherWikiUpdateRoute,
} from '~/paths';
import { createRefetchQueries } from '~/utils/graphql';

export const UPLOAD_OTHER_WIKI_LIST_PAGE = graphql(`
  query UPLOAD_OTHER_WIKI_LIST_PAGE {
    otherWikiList(first: 999999) {
      results {
        ...otherWikiParts
      }
    }
  }
`);

export default function UploadOtherWikiListPage() {
  return (
    <ApolloProviderV4>
      <UploadOtherWikiListPageInner />
    </ApolloProviderV4>
  );
}

function UploadOtherWikiListPageInner() {
  const { data, loading } = useQuery(UPLOAD_OTHER_WIKI_LIST_PAGE);

  const refetchQueries = createRefetchQueries([
    { query: UPLOAD_OTHER_WIKI_LIST_PAGE },
  ]);

  const otherList = data?.otherWikiList?.results ?? [];
  const {
    items,
    sortIndicatorProps: siProps,
    filterSearchProps,
  } = useSortFilter(otherList, 'title', 'title', 'uploadOtherList');

  function handleDeleteSuccess() {
    toast.info('Other wiki deleted successfully.');
  }

  if (loading) return <SpinnerPlaceholder />;

  return (
    <>
      <PageHeading>Other Wiki Pages</PageHeading>

      <Panel>
        <Panel.Heading className="flex justify-between align-center">
          <Panel.Title>
            Other Wiki Pages
            {!loading && (
              <small className="text-muted ml-2">
                (<strong>{items.length}</strong> results displayed)
              </small>
            )}
          </Panel.Title>

          <Link
            to={uploadOtherWikiCreateRoute()}
            className="btn btn-primary btn-xs"
          >
            <FontAwesomeIcon icon={faPlus} /> Create Wiki Page
          </Link>
        </Panel.Heading>

        <Panel.Body>
          <FilterSearch {...filterSearchProps} />

          <table className="table table-compact w-full">
            <thead>
              <tr>
                <th>
                  <SortTrigger colName="id" sortIndicatorProps={siProps}>
                    ID
                  </SortTrigger>
                </th>
                <th>
                  <SortTrigger colName="title" sortIndicatorProps={siProps}>
                    Title
                  </SortTrigger>
                </th>
                <th />
              </tr>
            </thead>

            <tbody>
              <NoItemsRow show={!items.length} />

              {items.map(other => (
                <tr key={other.id}>
                  <td>{other.id}</td>
                  <td>
                    <Link
                      to={uploadOtherWikiUpdateRoute(parseInt(other.id))}
                      className="link"
                    >
                      {other.title}
                    </Link>
                  </td>
                  <td className="text-right">
                    <div className="join">
                      <Link
                        to={uploadOtherWikiUpdateRoute(parseInt(other.id))}
                        className="btn btn-ghost btn-sm gap-1 join-item"
                      >
                        <FontAwesomeIcon icon={faPencil} /> Edit
                      </Link>

                      <DropdownMenu color="ghost" size="sm" join>
                        <DropdownMenu.Items>
                          <DropdownMenu.Item>
                            <Link
                              to={otherWikiDetailRoute(parseInt(other.id))}
                              target="_blank"
                              className="cursor-pointer"
                            >
                              <FontAwesomeIcon icon={faExternalLink} /> View
                            </Link>
                          </DropdownMenu.Item>

                          <DeleteOtherWiki
                            otherWikiId={parseInt(other.id)}
                            refetchQueries={refetchQueries}
                            onDeleteSuccess={handleDeleteSuccess}
                          >
                            {handleDelete => (
                              <Confirm
                                onConfirm={handleDelete}
                                submitButtonColor="error"
                                submitText="Delete Wiki Page"
                                text={
                                  <div className="space-y-2">
                                    <p>
                                      The other wiki page{' '}
                                      <strong>
                                        {other.title} (ID: {other.id})
                                      </strong>{' '}
                                      will be permanently deleted.
                                    </p>
                                    <Alert status="error">
                                      <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className="text-2xl"
                                      />
                                      <div>
                                        <Heading level={4}>WARNING</Heading>
                                        Some wiki pages are hard-linked
                                        throughout Safari by their ID. Deleting
                                        a wiki page may require a code update to
                                        change or remove the link.
                                      </div>
                                    </Alert>
                                  </div>
                                }
                              >
                                {confirmDelete => (
                                  <DropdownMenu.Item>
                                    <a
                                      onClick={confirmDelete}
                                      className="cursor-pointer"
                                    >
                                      <FontAwesomeIcon icon={faTrash} /> Delete
                                    </a>
                                  </DropdownMenu.Item>
                                )}
                              </Confirm>
                            )}
                          </DeleteOtherWiki>
                        </DropdownMenu.Items>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel.Body>
      </Panel>
    </>
  );
}
